import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';

// Types
export type LayoutContextValue = {
  breakpoint: 'mobile' | 'narrow' | 'wide';
  // NavBar
  globalNavOpen: boolean;
  setGlobalNavOpen: (setter: (prev: boolean) => boolean) => void;
};
// Constants
const defaultContextValue: LayoutContextValue = {
  breakpoint: 'mobile',
  globalNavOpen: false,
  setGlobalNavOpen: () => {},
};

// Contexts
export const LayoutContext = createContext(defaultContextValue);

// Hooks
const useStableBreakpointValue = () => {
  // This is more reliable than Chakra's `useBreakpointValue`, which tends to "flicker" more
  // between breakpoints
  const mobile = useMediaQuery({ minWidth: 0, maxWidth: 991 }) && 'mobile';
  const narrow = useMediaQuery({ minWidth: 992, maxWidth: 1279 }) && 'narrow';
  const wide = useMediaQuery({ minWidth: 1280 }) && 'wide';

  const [breakpoint, setBreakpoint] = useState<
    LayoutContextValue['breakpoint']
  >('mobile');

  useEffect(() => {
    // Sometimes multiple queries come back as true, so always pick the wider breakpoint
    const nextBreakpoint = wide || narrow || mobile;
    // Sometimes all three queries come back as false for a brief moment, skip the update
    if (!nextBreakpoint) return;
    setBreakpoint(nextBreakpoint);
  }, [mobile, narrow, wide]);

  return breakpoint;
};

// Exports
type LayoutContextProviderProps = { children: React.ReactNode };
export const LayoutContextProvider = ({
  children,
}: LayoutContextProviderProps) => {
  const [globalNavOpen, setGlobalNavOpen] = useState(false);
  const breakpoint = useStableBreakpointValue();

  const value = useMemo(
    () => ({ breakpoint, globalNavOpen, setGlobalNavOpen }),
    [breakpoint, globalNavOpen],
  );
  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayoutContext = () => useContext(LayoutContext);
